import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import { Loader } from '@paysense-india-services/elevate-web'

import { ProductConfigContext } from '@/contexts/ProductConfigContext'

import { captureReturnUrl } from '@/utils/captureReturnUrl'
import { isApplicationRoute } from '@/utils/navigation'
import { withProductParams } from '@/utils/queryParams'
import { resolveSource } from '@/utils/resolveSource'

import { buildProductConfig, LP_WEBSITE_URL, PRODUCT_SOURCES } from '@/config/products'

const ProductConfigProvider = ({ children }) => {
  const [searchParams] = useSearchParams()
  const [ready, setReady] = useState(false)
  const [source, setSource] = useState(null)
  const [returnUrl, setReturnUrl] = useState(null)

  useEffect(() => {
    const resolvedSource = resolveSource(searchParams.get('source'))
    setReturnUrl(captureReturnUrl(searchParams.get('returnUrl')))

    setSource(resolvedSource)
    setReady(true)
  }, [searchParams])

  const config = useMemo(() => (source ? buildProductConfig(source) : null), [source])

  const value = useMemo(() => {
    if (!config) return null

    const resolveReturnDestination = destination => {
      if (isApplicationRoute(destination)) {
        return `${destination}?${withProductParams({ source: config.source, returnUrl: null }).toString()}`
      }
      return destination
    }

    const resolveDefaultReturnUrl = emailVerifiedFallback =>
      resolveReturnDestination(
        returnUrl ??
          (emailVerifiedFallback && config.source === PRODUCT_SOURCES.LP && LP_WEBSITE_URL
            ? LP_WEBSITE_URL
            : config.defaultReturnPath)
      )

    return {
      config,
      source: config.source,
      returnUrl,
      appendProductParams: extra => withProductParams({ source: config.source, returnUrl }, extra),
      getReturnUrl: () => resolveDefaultReturnUrl(false),
      getEmailVerifiedReturnUrl: () => resolveDefaultReturnUrl(true)
    }
  }, [config, returnUrl])

  if (!ready || !value) {
    return (
      <div className="ew-d-flex ew-align-items-center ew-justify-content-center ew-h-100">
        <Loader color="brand" loadingText="Please Wait..." pageLoader size="large" />
      </div>
    )
  }

  return <ProductConfigContext.Provider value={value}>{children}</ProductConfigContext.Provider>
}

export default ProductConfigProvider
